import React, { useState, useEffect, useMemo } from "react";
import {
  TextField,
  Button,
  Grid,
  Container,
  Snackbar,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Typography,
  Autocomplete,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import apiService from "../components/ReasuableFuncations/api";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Add geocoder configuration at the top of the file
const geocoder = {
  async geocode(address) {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }
};

const ProjectManagement = ({ onSubmit }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const project = location.state?.project || null;

  const [userData, setUserData] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [geoScope, setGeoScope] = useState("");
  const [organization, setOrganization] = useState("");
  const [status, setStatus] = useState("");
  const [climate, setClimate] = useState("");
  const [climateOptions, setClimateOptions] = useState([
    "Infrastructure",
    "Legal and Regulatory Framework",
    "ICT Services and Digital Skills",
    "Climate Action",
    "Financial Stability",
    "Others",
  ]);
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [geoScopeOptions, setGeoScopeOptions] = useState([]);
  const [isOrgDisabled, setIsOrgDisabled] = useState(false);
  const [isgeoScopeDisabled, setIsgeoScopeDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [funding, setFunding] = useState([]); 
  const [fundingOptions, setFundingOptions] = useState([  // Changed to useState
    "State Contributions",
    "Public Sector",
    "Public - Private Partnership",
    "Development Partner",
    "Others"
  ]);
  const [fundingDescription, setFundingDescription] = useState("");
  const [hasRisk, setHasRisk] = useState(false);
  // Risk state
  const [riskErrors, setRiskErrors] = useState({});
  const [owners, setOwners] = useState([]);
  const [riskData, setRiskData] = useState({
    description: "",
    type: "",
    probability: "",
    impact: "",
    mitigationStrategy: "",
    owner: "",
    status: "",
    reviewDate: "",
    projectName: "",
    comments: "",
  });

  const [collaboratingRegions, setCollaboratingRegions] = useState([]);
  const [collaboratingStates, setCollaboratingStates] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);

  const fetchOwners = async (userData) => {
    if (!userData) {
      console.error("User data not found. Redirecting to login.");
      return;
    }
    const { regionalNetworkOption, countryOption } = userData;
    let url = `/api/projects-risk`;
    try {
      let response;
      response = await apiService.get(
        `${url}?regionalNetwork=${regionalNetworkOption}&country=${countryOption}`
      );

      if (response && response.statusText !== "OK") {
        throw new Error("Failed to fetch stakeholders");
      }

      const data = response.data;
      setOwners(data);
    } catch (error) {
      console.error("Error fetching stakeholders:", error);
    }
  };

  const handleRiskInputChange = (event) => {
    const { name, value } = event.target;
    setRiskData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (riskErrors[name]) {
      setRiskErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "",
      }));
    }
  };

  const handleFundingChange = (event, newValue) => {
    if (!newValue) {
      setFunding([]);
      return;
    }
    setFunding(newValue);
    
    // Add any new values to options
    newValue.forEach(value => {
      if (!fundingOptions.includes(value)) {
        setFundingOptions(prev => [...prev, value]);
      }
    });
  };

  const handleClimateChange = (event, newValue) => {
    if (!newValue) {
      setClimate("");
      return;
    }
    if (!climateOptions.includes(newValue)) {
      setClimateOptions([...climateOptions, newValue]);
    }
    setClimate(newValue);
  };

  const validateRiskForm = () => {
    const newErrors = {};
    if (!riskData.description.trim())
      newErrors.description = "Description is required";
    if (!riskData.type) newErrors.type = "Type is required";
    if (!riskData.probability)
      newErrors.probability = "Probability is required";
    if (!riskData.impact) newErrors.impact = "Impact is required";
    if (!riskData.mitigationStrategy.trim())
      newErrors.mitigationStrategy = "Mitigation strategy is required";
    if (!riskData.owner) newErrors.owner = "Risk owner is required";
    if (!riskData.status) newErrors.status = "Status is required";
    if (!riskData.reviewDate) newErrors.reviewDate = "Review date is required";
    setRiskErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitRisk = async (projectId) => {
    if (validateRiskForm()) {
      setLoading(true); // Start loading

      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) {
        console.error("User data not found. Redirecting to login.");
        setLoading(false); // Stop loading
        return;
      }
      const {
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
      } = userData;

      const requestData = {
        ...riskData,
        selectedOption,
        regionalNetworkOption,
        memberStateOption,
        countryOption,
        projectName: projectId,
      };

      try {
        const response = await apiService.post(`/api/risks`, requestData);

        if (response.statusText === "Created") {
          setRiskData({
            description: "",
            type: "",
            probability: "",
            impact: "",
            mitigationStrategy: "",
            owner: "",
            status: "",
            reviewDate: "",
            projectName: "",
            comments: "",
          });
        }
      } catch (error) {
        console.error("Error submitting risk data:", error);
      } finally {
        setLoading(false); // Stop loading after completion
      }
    }
  };

  const regionGeoScopes = useMemo(
    () => ({
      COMESA: [
        "Burundi",
        "Comoros",
        "DRC",
        "Djibouti",
        "Egypt",
        "Eritrea",
        "Eswatini",
        "Ethiopia",
        "Kenya",
        "Libya",
        "Madagascar",
        "Malawi",
        "Mauritius",
        "Rwanda",
        "Seychelles",
        "Somalia",
        "South Sudan",
        "Sudan",
        "Zambia",
        "Zimbabwe",
        "Tanzania",
      ],
      EAC: ["Burundi", "Kenya", "Rwanda", "South Sudan", "Tanzania", "Uganda","Congo","Somalia"],
      IGAD: [
        "Djibouti",
        "Eritrea",
        "Ethiopia",
        "Kenya",
        "Somalia",
        "South Sudan",
        "Sudan",
        "Uganda",
      ],
      IOC: [
        "Comoros",
        "France (Réunion)",
        "Madagascar",
        "Mauritius",
        "Seychelles",
      ],
      SADC: [
        "Angola",
        "Botswana",
        "Comoros",
        "DRC",
        "Eswatini",
        "Lesotho",
        "Madagascar",
        "Malawi",
        "Mozambique",
        "Namibia",
        "Seychelles",
        "South Africa",
        "Tanzania",
        "Zambia",
        "Zimbabwe",
      ],
    }),
    []
  );

  useEffect(() => {
    const userDataFromStorage = JSON.parse(localStorage.getItem("user"));

    setUserData(userDataFromStorage);

    fetchOwners(userDataFromStorage);

    if (project) {
      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toISOString().split("T")[0];
      };

      setProjectName(project.projectName || "");
      setDescription(project.description || "");
      setStartDate(project.startDate ? formatDate(project.startDate) : "");
      setEndDate(project.endDate ? formatDate(project.endDate) : "");
      setGeoScope(project.countryOption || "");
      setOrganization(project.regionalNetworkOption || "");
      setStatus(project.status || "");
      setClimate(project.climate || "");
      setFunding(project.funding ? project.funding.split(', ') : []);
      setFundingDescription(project.fundingDescription || "");
    }

    if (userDataFromStorage) {
      switch (userDataFromStorage.selectedOption) {
        case "Comesa":
          setGeoScopeOptions(regionGeoScopes.COMESA);
          setOrganization("COMESA");
          setIsOrgDisabled(false);
          setIsgeoScopeDisabled(false);
          break;
        case "Regional Network":
          const selectedRegion1 = userDataFromStorage.regionalNetworkOption;
          setGeoScopeOptions(regionGeoScopes[selectedRegion1] || []);
          setOrganization(userDataFromStorage.regionalNetworkOption);
          setIsOrgDisabled(true);
          setIsgeoScopeDisabled(false);
          break;
        case "Member State":
          const selectedRegion2 = userDataFromStorage.countryOption;
          setGeoScopeOptions([selectedRegion2]);
          setOrganization(userDataFromStorage.memberStateOption);
          setIsgeoScopeDisabled(true);
          setIsOrgDisabled(true);
          setGeoScope(selectedRegion2);
          break;
        default:
          break;
      }
    }
  }, [project, regionGeoScopes]);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Update available states when collaborating regions change
    const newAvailableStates = collaboratingRegions.reduce((acc, region) => {
      if (regionGeoScopes[region]) {
        return [...acc, ...regionGeoScopes[region]];
      }
      return acc;
    }, []);
    setAvailableStates([...new Set(newAvailableStates)]);
  }, [collaboratingRegions, regionGeoScopes]);

  const handleOrganizationChange = (e) => {
    if (!isOrgDisabled) {
      const selectedOrg = e.target.value;
      setOrganization(selectedOrg);

      if (selectedOrg && regionGeoScopes[selectedOrg]) {
        setGeoScopeOptions(regionGeoScopes[selectedOrg]);
      } else {
        setGeoScopeOptions([]);
      }
      setGeoScope("");
    }
  };

  const handleCollaboratingStatesChange = async (event, newValue) => {
    try {
      // Ensure newValue is always an array
      const statesArray = Array.isArray(newValue) ? newValue : [];
      
      const geocodedStates = await Promise.all(
        statesArray.map(async (state) => {
          const stateName = typeof state === 'string' ? state : state.name;
          try {
            const geoData = await geocoder.geocode(stateName);
            if (geoData) {
              return {
                name: stateName,
                latitude: geoData.latitude,
                longitude: geoData.longitude
              };
            }
          } catch (error) {
            console.error(`Error geocoding state ${stateName}:`, error);
          }
          return null;
        })
      );

      const validStates = geocodedStates.filter(Boolean);
      setCollaboratingStates(validStates);
    } catch (error) {
      console.error('Error handling collaborating states:', error);
      setCollaboratingStates([]);
    }
  };

  const clearFormData = () => {
    setProjectName("");
    setDescription("");
    setStartDate("");
    setEndDate("");
    setStatus("");
    setClimate("");
    setFunding([]);
    setFundingDescription("");
    setCollaboratingRegions([]);
    setCollaboratingStates([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const newErrors = {};
    if (!projectName) newErrors.projectName = "Project Name is required";
    if (!startDate || !endDate)
      newErrors.dates = "Start and End Dates are required";
    if (new Date(startDate) > new Date(endDate))
      newErrors.dates = "End Date must be after Start Date";
    if (!organization) newErrors.organization = "Organization is required";
    if (!geoScope) newErrors.geoScope = "Geographical Scope is required";
    if (!status) newErrors.status = "Status is required";
    if (!climate) newErrors.climate = "Climate Action and metrics are required";
    if (funding.length === 0) newErrors.funding = "Funding Type is required";
    if (!fundingDescription)
      newErrors.fundingDescription = "Funding type description is required";
    if (collaboratingRegions.length === 0) 
      newErrors.collaboratingRegions = "Collaborating Regions are required";
    if (collaboratingStates.length === 0) 
      newErrors.collaboratingStates = "Collaborating States are required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      
      // Show all validation errors in Snackbar
      const errorMessages = Object.values(newErrors).join(', ');
      setSnackbarMessage(`Please fix the following errors: ${errorMessages}`);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    const projectData = {
      projectName,
      description,
      startDate,
      endDate,
      geoScope,
      organization,
      status,
      climate,
      selectedOption: userData.selectedOption,
      regionalNetworkOption: userData.regionalNetworkOption,
      memberStateOption: userData.memberStateOption,
      countryOption: userData.countryOption,
      funding: funding.join(', '), // Join array elements with comma and space
      fundingDescription,
      collaboratingRegions: Array.isArray(collaboratingRegions) ? collaboratingRegions : [],
      collaboratingStates: Array.isArray(collaboratingStates) 
        ? collaboratingStates.map(state => ({
            name: state.name || state,
            latitude: state.latitude || 0,
            longitude: state.longitude || 0
          }))
        : []
    };

    try {
      const url = `/api/projects`;

      const response = project
        ? await apiService.put(url, projectData)
        : await apiService.post(url, projectData);

      if (response.statusText !== "OK") {
        const errorData = response.data;
        throw new Error(errorData.message || "Error processing project");
      }

      const responseData = response.data;
      if (hasRisk) await handleSubmitRisk(responseData.project._id);
      setSnackbarMessage(
        project
          ? "Successfully updated project!"
          : "Successfully created project!"
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      onSubmit(responseData);

      clearFormData();
    } catch (error) {
      setSnackbarMessage(
        error.message || "Error processing project.   try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ paddingRight: "40px" }}>
      <Paper elevation={20} sx={{ p: 4, mt: 4 }}>
        <form onSubmit={handleSubmit}>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                margin: "10px",
                right: "10px",
                top: "10px",
              }}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Project Name*"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                fullWidth
                error={!!errors.projectName}
                helperText={errors.projectName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Start Date*"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                error={!!errors.dates}
                helperText={errors.dates}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="End Date*"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                error={!!errors.dates}
                helperText={errors.dates}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.organization}>
                <InputLabel>Region*</InputLabel>
                <Select
                  value={organization}
                  label="Region"
                  onChange={handleOrganizationChange}
                  disabled={isOrgDisabled}
                >
                  <MenuItem value="COMESA">COMESA</MenuItem>
                  <MenuItem value="EAC">EAC</MenuItem>
                  <MenuItem value="IGAD">IGAD</MenuItem>
                  <MenuItem value="IOC">IOC</MenuItem>
                  <MenuItem value="SADC">SADC</MenuItem>
                </Select>
                <FormHelperText>{errors.organization}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.funding}>
                <Autocomplete
                  multiple
                  value={funding}
                  onChange={(event, newValue) => setFunding(newValue)}
                  options={fundingOptions}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Funding Type*"
                      variant="outlined"
                      error={!!errors.funding}
                      helperText={errors.funding}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Funding Type description*"
                value={fundingDescription}
                onChange={(e) => setFundingDescription(e.target.value)}
                fullWidth
                error={!!errors.fundingDescription}
                helperText={errors.fundingDescription}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.geoScope}>
                <InputLabel>Country/State*</InputLabel>
                <Select
                  value={geoScope}
                  label="Country/State"
                  onChange={(e) => setGeoScope(e.target.value)}
                  disabled={isgeoScopeDisabled}
                >
                  {geoScopeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.geoScope}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status*</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="Ongoing">Ongoing</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
                <FormHelperText>{errors.status}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.climate}>
                <Autocomplete
                  value={climate}
                  onChange={handleClimateChange}
                  options={climateOptions}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ICT Thematic Area"
                      variant="outlined"
                      error={!!errors.climate}
                      required
                    />
                  )}
                />
                <FormHelperText>{errors.climate}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.collaboratingRegions}>
                <Autocomplete
                  multiple
                  value={collaboratingRegions || []}
                  onChange={(event, newValue) => setCollaboratingRegions(newValue || [])}
                  options={Object.keys(regionGeoScopes)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Collaborating Regions*"
                      variant="outlined"
                      error={!!errors.collaboratingRegions}
                      helperText={errors.collaboratingRegions}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.collaboratingStates}>
                <Autocomplete
                  multiple
                  value={collaboratingStates || []}
                  onChange={handleCollaboratingStatesChange}
                  options={availableStates.map(state => ({
                    name: state,
                    latitude: 0,
                    longitude: 0
                  }))}
                  getOptionLabel={(option) => (option ? option.name : '')}
                  isOptionEqualToValue={(option, value) => {
                    return option?.name === value?.name;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Collaborating States*"
                      variant="outlined"
                      error={!!errors.collaboratingStates}
                      helperText={errors.collaboratingStates}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          {/* <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="hasRisk"
                  checked={hasRisk}
                  onChange={() => setHasRisk(!hasRisk)}
                />
              }
              label={
                <Typography sx={{ my: 2 }}>
                  Click to add project risks.
                </Typography>
              }
            />
          </Grid> */}

          {/* Risk management form*/}
          {/* {hasRisk && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Risk Description"
                  name="description"
                  value={riskData.description}
                  onChange={handleRiskInputChange}
                  error={!!riskErrors.description}
                  helperText={riskErrors.description}
                  required
                  multiline
                  rows={2}
                  maxRows={10}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& textarea": { resize: "vertical" },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!riskErrors.type}>
                  <InputLabel>Risk Type*</InputLabel>
                  <Select
                    name="type"
                    value={riskData.type}
                    onChange={handleRiskInputChange}
                    label="Risk Type"
                    required
                  >
                    <MenuItem value="Financial">Financial</MenuItem>
                    <MenuItem value="Operational">Operational</MenuItem>
                    <MenuItem value="Strategic">Strategic</MenuItem>
                    <MenuItem value="External">External</MenuItem>
                  </Select>
                  {riskErrors.type && (
                    <Typography color="error">{riskErrors.type}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!riskErrors.probability}>
                  <InputLabel>Probability*</InputLabel>
                  <Select
                    name="probability"
                    value={riskData.probability}
                    onChange={handleRiskInputChange}
                    label="Probability"
                    required
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                  {riskErrors.probability && (
                    <Typography color="error">
                      {riskErrors.probability}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!riskErrors.impact}>
                  <InputLabel>Impact*</InputLabel>
                  <Select
                    name="impact"
                    value={riskData.impact}
                    onChange={handleRiskInputChange}
                    label="Impact"
                    required
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                  {riskErrors.impact && (
                    <Typography color="error">{riskErrors.impact}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!riskErrors.owner}>
                  <InputLabel>Risk Owner*</InputLabel>
                  <Select
                    name="owner"
                    value={riskData.owner}
                    onChange={handleRiskInputChange}
                    label="Risk Owner"
                    required
                  >
                    {owners.map((owner) => (
                      <MenuItem key={owner._id} value={owner._id}>
                        {owner.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {riskErrors.owner && (
                    <Typography color="error">{riskErrors.owner}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!riskErrors.status}>
                  <InputLabel>Status*</InputLabel>
                  <Select
                    name="status"
                    value={riskData.status}
                    onChange={handleRiskInputChange}
                    label="Status"
                    required
                  >
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="Closed">Closed</MenuItem>
                    <MenuItem value="Mitigated">Mitigated</MenuItem>
                  </Select>
                  {riskErrors.status && (
                    <Typography color="error">{riskErrors.status}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mitigation Strategy"
                  name="mitigationStrategy"
                  value={riskData.mitigationStrategy}
                  onChange={handleRiskInputChange}
                  error={!!riskErrors.mitigationStrategy}
                  helperText={riskErrors.mitigationStrategy}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Review Date"
                  name="reviewDate"
                  type="date"
                  value={riskData.reviewDate}
                  onChange={handleRiskInputChange}
                  error={!!riskErrors.reviewDate}
                  helperText={riskErrors.reviewDate}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Comments"
                  name="comments"
                  value={riskData.comments}
                  onChange={handleRiskInputChange}
                  multiline
                  // rows={2}
                  maxRows={4}
                />
              </Grid>
            </Grid>
          )} */}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </form>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProjectManagement;
